import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ExternalLink, RefreshCw, ShieldCheck, Search, AlertCircle, CheckCircle2 } from 'lucide-react';
import { createPublicClient, http, formatUnits } from 'viem';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Navbar, Footer } from '@/components/Layout';
import EpochCapacityPill from '@/components/EpochCapacityPill';

// ── Live Sepolia configuration (mirror of MARKETING_FACTS.md §1.10–1.12) ────
const SEPOLIA = {
  chainId: 84532,
  rpcUrl: 'https://sepolia.base.org',
  hook: '0x5662668FB18bc504e05f4809005f8caa104103BA',
  oracle: '0x2Ac226061aE5beFe61581140362129183d7a939e',
  mockKoji: '0xc11117EDEBf959Dd09CaDF0FC60701C5A32fF9Ce',
  signer: '0xDCB21cC5Fc3d335D79717aF2C2404F5c622c3441',
  arbiter: '0x4B31fAacDb6F2E854E8d92be3942ac19B15f10B2',
  slashTx: '0x694ffe5f5a35dbf8e1267110987f802ec6b4173772c4627f7495ca2e2ef1d276',
  oracleApplyTx: '0x8f65ad3b869e72efb95ddbbdcf311eec283b5cf2f252c7c7a5c0eb37d420417a',
  slashedAgentId: 1782659884n,
  oracleAgentId: 1782659997n,
  explorer: 'https://sepolia.basescan.org',
};

const SEPOLIA_CHAIN = {
  id: SEPOLIA.chainId,
  name: 'Base Sepolia',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: [SEPOLIA.rpcUrl] } },
};

// ── Live Mainnet configuration (mirror of MARKETING_FACTS.md §1.15–1.16, canonical Pair A, USDC-bound) ───
const MAINNET = {
  chainId: 8453,
  rpcUrl: 'https://mainnet.base.org',
  withdrawalGuard: '0xE3D4B3BA88f53C2D2a499cb46Cd4679dE6c2021E',
  hook: '0x05F2541827104393257FEc27C61b8Ea5373e0874',
  oracle: '0x2364493206Aa7435050780bf710F934189B51ed6',
  usdc: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  arbiter: '0xfba264c34d19CbFf2f51Cf719E702bc44822994d',
  signer: '0xd7827fF6dae2027D023B148F6d9EB9Ac544d4569',
  deployedOn: 'October 4, 2026',
  explorer: 'https://basescan.org',
};

const MAINNET_CHAIN = {
  id: MAINNET.chainId,
  name: 'Base',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: [MAINNET.rpcUrl] } },
};

const client = createPublicClient({
  chain: SEPOLIA_CHAIN,
  transport: http(SEPOLIA.rpcUrl),
});

const mainnetClient = createPublicClient({
  chain: MAINNET_CHAIN,
  transport: http(MAINNET.rpcUrl),
});

// ── Minimal ABIs ────────────────────────────────────────────────────────────
const HOOK_ABI = [
  { type: 'function', name: 'kojiToken',  stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'arbiter',    stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'oracle',     stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'owner',      stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'MIN_SCORE',  stateMutability: 'view', inputs: [], outputs: [{ type: 'uint16' }] },
  { type: 'function', name: 'MAX_SCORE',  stateMutability: 'view', inputs: [], outputs: [{ type: 'uint16' }] },
  { type: 'function', name: 'SLASH_RATIO',stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'getStake',   stateMutability: 'view', inputs: [{ name: 'agentId', type: 'uint256' }], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'getScore',   stateMutability: 'view', inputs: [{ name: 'agentId', type: 'uint256' }], outputs: [{ type: 'uint16' }] },
  { type: 'function', name: 'isSlashed',  stateMutability: 'view', inputs: [{ name: 'agentId', type: 'uint256' }], outputs: [{ type: 'bool' }] },
];

const ORACLE_ABI = [
  { type: 'function', name: 'signer',        stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'hookContract',  stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'owner',         stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'lastAppliedNonce', stateMutability: 'view', inputs: [{ name: 'agentId', type: 'uint256' }], outputs: [{ type: 'uint64' }] },
];

const KOJI_ABI = [
  { type: 'function', name: 'balanceOf', stateMutability: 'view', inputs: [{ name: 'account', type: 'address' }], outputs: [{ type: 'uint256' }] },
];

const GUARD_ABI = [
  { type: 'function', name: 'getStatus', stateMutability: 'view', inputs: [], outputs: [
      { name: '_enforcing', type: 'bool' },
      { name: '_epochStart', type: 'uint256' },
      { name: '_epochDuration', type: 'uint256' },
      { name: '_epochWithdrawn', type: 'uint256' },
      { name: '_epochLimit', type: 'uint256' },
      { name: '_tvlSnapshot', type: 'uint256' },
      { name: '_maxWithdrawBps', type: 'uint256' },
      { name: '_epochWithdrawalCount', type: 'uint256' },
      { name: '_totalLifetimeWithdrawn', type: 'uint256' },
      { name: '_totalEpochsElapsed', type: 'uint256' },
      { name: '_epochTimeRemaining', type: 'uint256' },
    ] },
  { type: 'function', name: 'owner', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
];

// ── Small UI atoms ──────────────────────────────────────────────────────────
const fmtAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '—');
const fmtKoji = (wei) => {
  if (wei == null) return '—';
  try { return `${formatUnits(wei, 18)} mKOJI`; } catch { return String(wei); }
};

function Mono({ children, href }) {
  const content = (
    <code className="text-xs font-mono break-all text-red-400 bg-red-500/5 px-1.5 py-0.5 rounded">{children}</code>
  );
  if (!href) return content;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="hover:underline">
      {content}
    </a>
  );
}

function StatRow({ label, value, mono, href, expected, ok }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-white/5 last:border-b-0">
      <div className="text-xs uppercase tracking-wider text-white/50 w-44 flex-shrink-0">{label}</div>
      <div className="flex-1 text-sm text-white/90">
        {mono ? <Mono href={href}>{value}</Mono> : value}
        {expected && (
          <span className="ml-2 text-xs text-white/40">
            {ok ? <CheckCircle2 className="inline w-3 h-3 text-emerald-400" /> : <AlertCircle className="inline w-3 h-3 text-red-400" />}
            {' '}expected {expected}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function OnChainReceipts() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wiring, setWiring] = useState(null);
  const [slashedState, setSlashedState] = useState(null);
  const [oracleState, setOracleState] = useState(null);
  const [refreshAt, setRefreshAt] = useState(null);
  const [guardState, setGuardState] = useState(null);
  const [guardError, setGuardError] = useState(null);

  // Lookup widget
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const fetchLiveState = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [
        kojiToken, arbiter, oracleAddr, hookOwner,
        oracleSigner, oracleHook, oracleOwner,
        // slashed agent
        slashedScore, slashedStake, slashedFlag,
        // oracle agent
        oracleAgentScore, oracleAgentNonce,
        // KOJI balances
        harmedBal, hookBal,
      ] = await Promise.all([
        client.readContract({ address: SEPOLIA.hook,   abi: HOOK_ABI,   functionName: 'kojiToken' }),
        client.readContract({ address: SEPOLIA.hook,   abi: HOOK_ABI,   functionName: 'arbiter' }),
        client.readContract({ address: SEPOLIA.hook,   abi: HOOK_ABI,   functionName: 'oracle' }),
        client.readContract({ address: SEPOLIA.hook,   abi: HOOK_ABI,   functionName: 'owner' }),
        client.readContract({ address: SEPOLIA.oracle, abi: ORACLE_ABI, functionName: 'signer' }),
        client.readContract({ address: SEPOLIA.oracle, abi: ORACLE_ABI, functionName: 'hookContract' }),
        client.readContract({ address: SEPOLIA.oracle, abi: ORACLE_ABI, functionName: 'owner' }),
        client.readContract({ address: SEPOLIA.hook,   abi: HOOK_ABI,   functionName: 'getScore',  args: [SEPOLIA.slashedAgentId] }),
        client.readContract({ address: SEPOLIA.hook,   abi: HOOK_ABI,   functionName: 'getStake',  args: [SEPOLIA.slashedAgentId] }),
        client.readContract({ address: SEPOLIA.hook,   abi: HOOK_ABI,   functionName: 'isSlashed', args: [SEPOLIA.slashedAgentId] }),
        client.readContract({ address: SEPOLIA.hook,   abi: HOOK_ABI,   functionName: 'getScore', args: [SEPOLIA.oracleAgentId] }),
        client.readContract({ address: SEPOLIA.oracle, abi: ORACLE_ABI, functionName: 'lastAppliedNonce', args: [SEPOLIA.oracleAgentId] }),
        client.readContract({ address: SEPOLIA.mockKoji, abi: KOJI_ABI, functionName: 'balanceOf', args: ['0x000000000000000000000000000000000000bEEF'] }),
        client.readContract({ address: SEPOLIA.mockKoji, abi: KOJI_ABI, functionName: 'balanceOf', args: [SEPOLIA.hook] }),
      ]);

      setWiring({
        kojiToken, arbiter, oracleAddr, hookOwner,
        oracleSigner, oracleHook, oracleOwner,
      });
      setSlashedState({ score: slashedScore, stake: slashedStake, slashed: slashedFlag, harmedBal, hookBal });
      setOracleState({ score: oracleAgentScore, nonce: oracleAgentNonce });
      setRefreshAt(new Date());

      // Mainnet WithdrawalGuard — run in parallel but isolate its failure
      try {
        const [status, owner] = await Promise.all([
          mainnetClient.readContract({ address: MAINNET.withdrawalGuard, abi: GUARD_ABI, functionName: 'getStatus' }),
          mainnetClient.readContract({ address: MAINNET.withdrawalGuard, abi: GUARD_ABI, functionName: 'owner' }),
        ]);
        setGuardState({
          enforcing: status[0],
          epochDuration: status[2],
          epochWithdrawn: status[3],
          epochLimit: status[4],
          tvlSnapshot: status[5],
          maxWithdrawBps: status[6],
          totalEpochsElapsed: status[9],
          owner,
        });
        setGuardError(null);
      } catch (ge) {
        setGuardError(ge.shortMessage || ge.message || String(ge));
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLiveState(); }, [fetchLiveState]);

  const runLookup = async () => {
    const id = lookupId.trim();
    if (!id || !/^[0-9]+$/.test(id)) {
      setLookupResult({ error: 'Enter a numeric agent ID (e.g. 1782659884).' });
      return;
    }
    setLookupLoading(true); setLookupResult(null);
    try {
      const aid = BigInt(id);
      const [score, stake, slashed] = await Promise.all([
        client.readContract({ address: SEPOLIA.hook, abi: HOOK_ABI, functionName: 'getScore',  args: [aid] }),
        client.readContract({ address: SEPOLIA.hook, abi: HOOK_ABI, functionName: 'getStake',  args: [aid] }),
        client.readContract({ address: SEPOLIA.hook, abi: HOOK_ABI, functionName: 'isSlashed', args: [aid] }),
      ]);
      setLookupResult({ id, score, stake, slashed });
    } catch (e) {
      setLookupResult({ error: e.shortMessage || e.message || String(e) });
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="onchain-receipts-page">
      <Helmet>
        <title>On-Chain Receipts · Kojiru ACS</title>
        <meta name="description" content="Live transaction receipts and contract state for the Kojiru Agent Credit Protocol. As of 7-20-2026, KojiruACSHook + KojiruACSOracle are live on Base Mainnet. The Sepolia bake receipts on this page remain as the proof trail. Every figure is read directly from the chain at page load." />
      </Helmet>
      <Navbar />
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-5xl mx-auto">

          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/10 border border-red-500/20 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-red-400" />
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-red-400 font-semibold">KOJIRU · LIVE ON-CHAIN</div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4" data-testid="onchain-receipts-heading">
              On-Chain Receipts
            </h1>
            <p className="text-base sm:text-lg text-white/70 max-w-3xl leading-relaxed">
              Every number on this page is read directly from <strong className="text-white">Base Sepolia (chain id 84532)</strong> at page load.
              No backend in between, no caching, no marketing varnish — just <code className="text-xs bg-white/5 px-1 py-0.5 rounded text-red-300">eth_call</code> against a public RPC.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <Button
                onClick={fetchLiveState}
                disabled={loading}
                variant="outline"
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                data-testid="onchain-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Reading chain…' : 'Re-read live state'}
              </Button>
              {refreshAt && (
                <span className="text-xs text-white/40" data-testid="onchain-refreshed-at">
                  Last read: {refreshAt.toLocaleTimeString()} ({refreshAt.toLocaleDateString()})
                </span>
              )}
            </div>
            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300 flex items-start gap-2" data-testid="onchain-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Could not read chain:</strong> {error}
                  <br />The public Sepolia RPC may be rate-limiting; click "Re-read" to retry.
                </div>
              </div>
            )}
          </motion.div>

          {/* Wiring panel */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <h2 className="text-lg font-semibold text-white/90 mb-3">Live Contract Wiring</h2>
            <Card className="bg-white/[0.02] border-white/10">
              <CardContent className="p-5">
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-2">KojiruACSHook</div>
                    <StatRow label="Address" mono value={SEPOLIA.hook} href={`${SEPOLIA.explorer}/address/${SEPOLIA.hook}`} />
                    <StatRow label="kojiToken()" mono value={wiring?.kojiToken ?? '—'} expected={fmtAddr(SEPOLIA.mockKoji)} ok={wiring?.kojiToken?.toLowerCase() === SEPOLIA.mockKoji.toLowerCase()} />
                    <StatRow label="arbiter()" mono value={wiring?.arbiter ?? '—'} expected={fmtAddr(SEPOLIA.arbiter)} ok={wiring?.arbiter?.toLowerCase() === SEPOLIA.arbiter.toLowerCase()} />
                    <StatRow label="oracle()" mono value={wiring?.oracleAddr ?? '—'} expected={fmtAddr(SEPOLIA.oracle)} ok={wiring?.oracleAddr?.toLowerCase() === SEPOLIA.oracle.toLowerCase()} />
                    <StatRow label="owner()" mono value={wiring?.hookOwner ?? '—'} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-red-400 font-semibold mb-2">KojiruACSOracle</div>
                    <StatRow label="Address" mono value={SEPOLIA.oracle} href={`${SEPOLIA.explorer}/address/${SEPOLIA.oracle}`} />
                    <StatRow label="signer()" mono value={wiring?.oracleSigner ?? '—'} expected={fmtAddr(SEPOLIA.signer)} ok={wiring?.oracleSigner?.toLowerCase() === SEPOLIA.signer.toLowerCase()} />
                    <StatRow label="hookContract()" mono value={wiring?.oracleHook ?? '—'} expected={fmtAddr(SEPOLIA.hook)} ok={wiring?.oracleHook?.toLowerCase() === SEPOLIA.hook.toLowerCase()} />
                    <StatRow label="owner()" mono value={wiring?.oracleOwner ?? '—'} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Receipts */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mt-10">
            <h2 className="text-lg font-semibold text-white/90 mb-3">Verified Live Receipts</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Slash receipt */}
              <Card className="bg-gradient-to-br from-red-500/[0.04] to-orange-500/[0.02] border-red-500/20" data-testid="slash-receipt-card">
                <CardContent className="p-5">
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="text-xs uppercase tracking-wider text-red-400 font-semibold">Atomic Slash · 1:100</div>
                    <a href={`${SEPOLIA.explorer}/tx/${SEPOLIA.slashTx}`} target="_blank" rel="noopener noreferrer" className="text-xs text-red-300 hover:underline flex items-center gap-1">
                      View tx <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <p className="text-sm text-white/70 mb-4">
                    Agent <Mono>{SEPOLIA.slashedAgentId.toString()}</Mono> was slashed for $1 of debt. Exactly <strong className="text-white">100 mKOJI</strong> routed atomically to harmed party.
                  </p>
                  <StatRow label="Agent score" value={slashedState ? `${slashedState.score} (floor)` : '—'} expected="300" ok={slashedState?.score === 300} />
                  <StatRow label="Agent stake remaining" value={fmtKoji(slashedState?.stake)} expected="100 mKOJI" ok={slashedState && slashedState.stake === 100000000000000000000n} />
                  <StatRow label="isSlashed flag" value={slashedState?.slashed === true ? 'true ✓' : slashedState?.slashed === false ? 'false ✗' : '—'} expected="true" ok={slashedState?.slashed === true} />
                  <StatRow label="Harmed party mKOJI" value={fmtKoji(slashedState?.harmedBal)} expected="100 mKOJI" ok={slashedState && slashedState.harmedBal === 100000000000000000000n} />
                  <StatRow label="Hook mKOJI balance" value={fmtKoji(slashedState?.hookBal)} />
                </CardContent>
              </Card>

              {/* Oracle receipt */}
              <Card className="bg-gradient-to-br from-emerald-500/[0.04] to-teal-500/[0.02] border-emerald-500/20" data-testid="oracle-receipt-card">
                <CardContent className="p-5">
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">EIP-712 Signed Update</div>
                    <a href={`${SEPOLIA.explorer}/tx/${SEPOLIA.oracleApplyTx}`} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-300 hover:underline flex items-center gap-1">
                      View tx <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <p className="text-sm text-white/70 mb-4">
                    Backend signer <Mono>{fmtAddr(SEPOLIA.signer)}</Mono> produced an off-chain EIP-712 attestation. The oracle verified the signature on-chain and pushed agent <Mono>{SEPOLIA.oracleAgentId.toString()}</Mono>'s score 600 → 780.
                  </p>
                  <StatRow label="Agent score" value={oracleState?.score?.toString() ?? '—'} expected="780" ok={oracleState?.score === 780} />
                  <StatRow label="lastAppliedNonce" value={oracleState?.nonce?.toString() ?? '—'} expected="1" ok={oracleState?.nonce === 1n} />
                  <StatRow label="Replay-safe" value="nonce monotonic + expiry enforced" />
                  <StatRow label="Digest agreement" value="byte-equal to oracle.digestFor()" />
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Agent lookup widget */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-10">
            <h2 className="text-lg font-semibold text-white/90 mb-3">Look Up Any Agent</h2>
            <Card className="bg-white/[0.02] border-white/10">
              <CardContent className="p-5">
                <p className="text-sm text-white/60 mb-3">
                  Enter an agent ID to read its current credit score, posted collateral, and slashed status directly from the chain.
                </p>
                <div className="flex gap-2 mb-4">
                  <Input
                    type="text"
                    placeholder="e.g. 1782659884"
                    value={lookupId}
                    onChange={(e) => setLookupId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && runLookup()}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 font-mono"
                    data-testid="agent-lookup-input"
                  />
                  <Button
                    onClick={runLookup}
                    disabled={lookupLoading}
                    className="bg-red-500/80 hover:bg-red-500 text-white border-0"
                    data-testid="agent-lookup-submit"
                  >
                    <Search className="w-4 h-4 mr-1" />
                    {lookupLoading ? 'Reading…' : 'Look up'}
                  </Button>
                </div>
                {lookupResult?.error && (
                  <div className="text-sm text-red-300 p-3 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="agent-lookup-error">
                    {lookupResult.error}
                  </div>
                )}
                {lookupResult && !lookupResult.error && (
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4" data-testid="agent-lookup-result">
                    <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Agent {lookupResult.id}</div>
                    <StatRow label="Credit score" value={lookupResult.score?.toString() ?? '—'} />
                    <StatRow label="Posted collateral" value={fmtKoji(lookupResult.stake)} />
                    <StatRow label="isSlashed" value={lookupResult.slashed === true ? '⚠️ true (slashed)' : '✓ false (good standing)'} />
                  </div>
                )}
                <div className="mt-4 text-xs text-white/40">
                  Try ID <button onClick={() => setLookupId('1782659884')} className="text-red-300 hover:underline">1782659884</button> (the slashed agent) or <button onClick={() => setLookupId('1782659997')} className="text-emerald-300 hover:underline">1782659997</button> (the oracle-updated agent).
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Verify-it-yourself */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-10">
            <h2 className="text-lg font-semibold text-white/90 mb-3">Verify it Yourself (curl, 30 seconds)</h2>
            <Card className="bg-[#0a0d18] border-white/10">
              <CardContent className="p-0">
                <pre className="text-[11px] leading-relaxed font-mono text-white/70 p-4 overflow-x-auto whitespace-pre-wrap">
{`# Read agent ${SEPOLIA.slashedAgentId} state on Sepolia. Should return score=300, slashed=true.
curl -s -X POST ${SEPOLIA.rpcUrl} \\
  -H 'Content-Type: application/json' \\
  -d '{"jsonrpc":"2.0","method":"eth_chainId","params":[],"id":1}'
# → expect "0x14a34" (84532 = Base Sepolia)

# Slash tx receipt:
curl -s -X POST ${SEPOLIA.rpcUrl} \\
  -H 'Content-Type: application/json' \\
  -d '{"jsonrpc":"2.0","method":"eth_getTransactionReceipt","params":["${SEPOLIA.slashTx}"],"id":2}'
# → status "0x1" (success), logs[].address = Hook + MockKOJI`}
                </pre>
              </CardContent>
            </Card>
            <div className="mt-4 text-xs text-white/40">
              Or skip the terminal and just{' '}
              <a href="/kojiru-slash-proof-onepager.pdf" target="_blank" rel="noopener noreferrer" className="text-red-300 hover:underline" data-testid="download-slash-proof-link">
                download the printable slash-proof one-pager (PDF) <ExternalLink className="inline w-3 h-3" />
              </a>
              .
            </div>
          </motion.div>

          {/* Mainnet WithdrawalGuard — live read */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="mt-10" data-testid="withdrawal-guard-section">
            <h2 className="text-lg font-semibold text-white/90 mb-3">Mainnet · WithdrawalGuard (live read)</h2>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="md:col-span-2">
                <Card className="bg-gradient-to-br from-emerald-500/[0.04] to-teal-500/[0.02] border-emerald-500/20 h-full" data-testid="withdrawal-guard-card">
              <CardContent className="p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Base Mainnet (8453) · Rate-Limiter</div>
                  <a
                    href={`${MAINNET.explorer}/address/${MAINNET.withdrawalGuard}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-emerald-300 hover:underline flex items-center gap-1"
                    data-testid="withdrawal-guard-explorer-link"
                  >
                    View on BaseScan <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <p className="text-sm text-white/70 mb-4">
                  Live <code className="text-xs bg-white/5 px-1 py-0.5 rounded text-emerald-300">eth_call</code> against Base mainnet. The cap protects the future lender pool — no withdrawals have routed through it yet because the pool surface it gates is not user-active. The contract is configured and enforcing on-chain today.
                </p>
                <StatRow label="Address" mono value={MAINNET.withdrawalGuard} href={`${MAINNET.explorer}/address/${MAINNET.withdrawalGuard}`} />
                <StatRow label="enforcing()" value={guardState ? (guardState.enforcing ? 'true \u2713' : 'false \u2717') : '\u2014'} expected="true" ok={guardState?.enforcing === true} />
                <StatRow label="epochDuration" value={guardState ? `${guardState.epochDuration} sec (${(Number(guardState.epochDuration) / 3600).toFixed(0)} h)` : '\u2014'} expected="86400 (24h)" ok={guardState && Number(guardState.epochDuration) === 86400} />
                <StatRow label="maxWithdrawBps" value={guardState ? `${guardState.maxWithdrawBps} (= ${Number(guardState.maxWithdrawBps) / 100}%/epoch)` : '\u2014'} expected="1000 (10%)" ok={guardState && Number(guardState.maxWithdrawBps) === 1000} />
                <StatRow label="epochLimit" value={guardState ? `$${(Number(guardState.epochLimit) / 100).toLocaleString()} (cents-denominated)` : '\u2014'} />
                <StatRow label="totalEpochsElapsed" value={guardState ? guardState.totalEpochsElapsed.toString() : '\u2014'} />
                <StatRow label="owner()" mono value={guardState?.owner ?? '\u2014'} />
                {guardError && (
                  <div className="mt-3 text-xs text-yellow-300 flex items-start gap-2" data-testid="withdrawal-guard-error">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    Could not read Base mainnet RPC: {guardError}. The contract is deployed at the address above; this is a read-side rate-limit on the public RPC \u2014 try the refresh button.
                  </div>
                )}
                <div className="mt-3 text-xs text-white/40">
                  Honest scope: deployed and enforcing on Base mainnet. <strong>Not formally verified.</strong> The Certora engagement is on the Q3 2026 roadmap (see MARKETING_FACTS §3).
                </div>
              </CardContent>
            </Card>
              </div>
              <div>
                <EpochCapacityPill variant="card" />
              </div>
            </div>
          </motion.div>

          {/* Mainnet deploy notice */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10">
            <Card className="bg-emerald-500/[0.05] border-emerald-500/30">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-white/80 leading-relaxed">
                    <strong className="text-white">Mainnet deploy complete (7-20-2026).</strong>{' '}
                    The same contracts shown above on Sepolia are now live on Base Mainnet, wired to the real KOJI token (
                    <Mono href={`${MAINNET.explorer}/token/${MAINNET.koji}`}>{fmtAddr(MAINNET.koji)}</Mono>).
                    Mainnet Hook: <Mono href={`${MAINNET.explorer}/address/${MAINNET.hook}`}>{fmtAddr(MAINNET.hook)}</Mono>{' '}
                    · Mainnet Oracle: <Mono href={`${MAINNET.explorer}/address/${MAINNET.oracle}`}>{fmtAddr(MAINNET.oracle)}</Mono>{' '}
                    · Deploy tx: <Mono href={`${MAINNET.explorer}/tx/${MAINNET.hookDeployTx}`}>view</Mono>.
                    The Sepolia bake receipts on this page remain as the proof trail: slash mechanic verified June 2026, wallet rotation July 18, post-rotation slash July 19, mainnet promotion July 20.
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Historical Sepolia bake context */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-4">
            <Card className="bg-white/[0.02] border-white/10">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-white/40 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-white/60 leading-relaxed">
                    <strong className="text-white/80">Sepolia context for the receipts above.</strong>{' '}
                    The collateral token referenced in these receipts (MockKOJI at <Mono href={`${SEPOLIA.explorer}/address/${SEPOLIA.mockKoji}`}>{fmtAddr(SEPOLIA.mockKoji)}</Mono>) is a permissionless test ERC-20 — anyone can mint it. It is <em>not</em> the real KOJI token; that binding happens on mainnet. Every state transition on this page is a Sepolia proof preserved for historical inspection.
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
